import React from "react";
import { useNavigate } from "react-router-dom";
import { useProfile } from "../contexts/ProfileContext";
import { useAuth } from "../contexts/AuthContext";
import { CLASSES } from "../data/classes";
import { PURCHASE_ONLY_CLASSES } from "../data/profile";
import { BORDERS, BANNERS, COSMETIC_RARITY_COLORS, BANNERS_BY_ID } from "../data/cosmetics";
import { ArrowLeft, Coins, Check, ShoppingBag, Sparkles, Lock, Image as ImageIcon } from "lucide-react";
import { SFX } from "../utils/audio";

const MenuShop = () => {
  const navigate = useNavigate();
  const { user, loginWithGoogle } = useAuth();
  const { profile, purchaseBorder, equipBorder, equipBanner } = useProfile();

  const owned = new Set(profile?.cosmetics_owned || []);
  owned.add("default");
  const equipped = profile?.equipped_border || "default";
  const available = profile?.meta_gold_available || 0;
  const earnedTotal = profile?.meta_gold_earned || 0;
  const spentTotal = profile?.meta_gold_spent || 0;

  const avatarClass = profile?.avatar_classe ? CLASSES[profile.avatar_classe] : null;
  const equippedBorder = BORDERS.find((b) => b.id === equipped) || BORDERS[0];

  return (
    <div className="h-screen w-screen bg-slate-900 bg-dots overflow-y-auto">
      <div className="max-w-5xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <button
            data-testid="menu-shop-back"
            onClick={() => { SFX.click(); navigate("/"); }}
            className="bg-slate-700 text-white border-4 border-slate-950 rounded-xl px-3 py-2 cartoon-shadow flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" /> Menu
          </button>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white flex items-center gap-2">
            <ShoppingBag className="w-7 h-7 text-yellow-400" /> Boutique
          </h1>
          <div className="w-20" />
        </div>

        {/* Wallet + preview */}
        <div className="grid sm:grid-cols-[180px_1fr] gap-4 mb-6">
          {/* Avatar preview with equipped border */}
          <div className="bg-slate-800 border-4 border-slate-950 rounded-2xl p-3 cartoon-shadow-lg-static text-center">
            <p className="font-display font-bold text-xs uppercase tracking-wider text-slate-400 mb-2">Aperçu</p>
            <div className={`w-24 h-24 sm:w-28 sm:h-28 rounded-2xl mx-auto overflow-hidden ${equippedBorder.style.className} ${equippedBorder.style.ring}`}>
              {avatarClass ? (
                <div className={`w-full h-full bg-gradient-to-br ${avatarClass.couleur}`}>
                  <img src={avatarClass.image} alt={avatarClass.nom} className="w-full h-full object-cover" />
                </div>
              ) : profile?.picture ? (
                <img src={profile.picture} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-slate-700 flex items-center justify-center text-4xl">?</div>
              )}
            </div>
            <p className="mt-2 font-display font-bold text-sm">{profile?.display_name || "Aventurier"}</p>
            <p className="text-[10px] text-slate-400">Bordure : {equippedBorder.name}</p>
          </div>

          {/* Wallet */}
          <div className="bg-slate-800 border-4 border-slate-950 rounded-2xl p-4 cartoon-shadow-lg-static">
            <h2 className="font-display font-bold text-base mb-3 flex items-center gap-2 text-yellow-400">
              <Coins className="w-5 h-5" /> Mon or de boutique
            </h2>
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-yellow-400 text-slate-950 border-2 border-slate-950 rounded-lg p-2 text-center">
                <p className="text-[10px] font-bold uppercase">Disponible</p>
                <p data-testid="menu-shop-available" className="font-display font-bold text-xl">{available}</p>
              </div>
              <div className="bg-slate-900 border-2 border-slate-950 rounded-lg p-2 text-center">
                <p className="text-[10px] font-bold uppercase text-emerald-400">Gagné total</p>
                <p data-testid="menu-shop-earned" className="font-display font-bold text-xl text-emerald-400">{earnedTotal}</p>
              </div>
              <div className="bg-slate-900 border-2 border-slate-950 rounded-lg p-2 text-center">
                <p className="text-[10px] font-bold uppercase text-rose-400">Dépensé</p>
                <p data-testid="menu-shop-spent" className="font-display font-bold text-xl text-rose-400">{spentTotal}</p>
              </div>
            </div>
            <p className="mt-2 text-[11px] text-slate-400 italic">
              L'or de boutique cumule l'or que tu collectes pendant tes runs (gardé même après ta mort).
            </p>
            {!profile?.authed && (
              <button onClick={loginWithGoogle} className="mt-2 bg-white text-slate-950 border-2 border-slate-950 rounded-lg px-3 py-1 text-xs font-bold">
                Se connecter pour sauvegarder
              </button>
            )}
          </div>
        </div>

        <h2 className="font-display font-bold text-lg mb-3 text-white flex items-center gap-2">
          <Lock className="w-5 h-5 text-amber-400" /> Classes spéciales
        </h2>
        <div data-testid="special-classes-grid" className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-7">
          {Object.entries(PURCHASE_ONLY_CLASSES).map(([classId, info]) => {
            const cls = CLASSES[classId];
            if (!cls) return null;
            const unlocked = owned.has(info.unlockId);
            const canAfford = available >= info.cost;
            return (
              <div key={classId} data-testid={`special-class-${classId}`} className={`bg-slate-800 border-4 border-slate-950 rounded-2xl p-3 cartoon-shadow-lg-static flex gap-3 ${unlocked ? "ring-4 ring-emerald-400" : ""}`}>
                <div className={`w-24 h-24 flex-shrink-0 rounded-xl overflow-hidden bg-gradient-to-br ${cls.couleur} border-2 border-slate-950`}>
                  <img src={cls.image} alt={cls.nom} className={`w-full h-full object-cover ${unlocked ? "" : "grayscale-[0.3]"}`} />
                </div>
                <div className="flex-1 min-w-0 flex flex-col">
                  <p className="font-display font-bold text-base">{cls.nom}</p>
                  <p className="text-xs text-slate-300 leading-tight flex-1">{cls.description}</p>
                  {unlocked ? (
                    <div className="mt-2 bg-emerald-500 text-white border-2 border-slate-950 rounded-lg py-1.5 font-display font-bold text-xs uppercase flex items-center justify-center gap-1"><Check className="w-3 h-3" /> Débloquée</div>
                  ) : (
                    <button data-testid={`buy-class-${classId}`} onClick={() => purchaseBorder(info.unlockId, info.cost)} disabled={!canAfford}
                      className={`mt-2 border-2 border-slate-950 rounded-lg py-1.5 font-display font-bold text-xs uppercase flex items-center justify-center gap-1 ${canAfford ? "bg-yellow-400 text-slate-950" : "bg-slate-700 text-slate-400 cursor-not-allowed"}`}>
                      <Coins className="w-3 h-3" /> {canAfford ? `Débloquer — ${info.cost.toLocaleString("fr-FR")} or` : `${info.cost.toLocaleString("fr-FR")} or requis`}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <h2 className="font-display font-bold text-lg mb-3 text-white flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-fuchsia-400" /> Bordures de profil
        </h2>

        <div data-testid="borders-grid" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {BORDERS.map((b) => {
            const isOwned = owned.has(b.id);
            const isEquipped = equipped === b.id;
            const canAfford = available >= b.price;
            const rarityClass = COSMETIC_RARITY_COLORS[b.rarity];
            return (
              <div
                key={b.id}
                data-testid={`border-card-${b.id}`}
                className={`bg-slate-800 border-4 border-slate-950 rounded-2xl p-3 cartoon-shadow-lg-static flex flex-col items-center ${isEquipped ? "ring-4 ring-yellow-400" : ""}`}
              >
                {/* Preview swatch with the avatar */}
                <div className={`w-20 h-20 rounded-xl overflow-hidden mb-2 ${b.style.className} ${b.style.ring}`}>
                  {avatarClass ? (
                    <div className={`w-full h-full bg-gradient-to-br ${avatarClass.couleur}`}>
                      <img src={avatarClass.image} alt="" className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className="w-full h-full bg-slate-700" />
                  )}
                </div>
                <p className="font-display font-bold text-sm leading-tight text-center">{b.name}</p>
                <p className={`text-[10px] font-bold uppercase tracking-wider ${rarityClass}`}>{b.rarity}</p>
                <p className="text-xs text-slate-400 text-center leading-tight mt-1">{b.description}</p>

                {isOwned ? (
                  isEquipped ? (
                    <div className="mt-2 w-full bg-yellow-400 text-slate-950 border-2 border-slate-950 rounded-lg py-1.5 font-display font-bold text-xs uppercase flex items-center justify-center gap-1">
                      <Check className="w-3 h-3" /> Équipée
                    </div>
                  ) : (
                    <button
                      data-testid={`equip-border-${b.id}`}
                      onClick={() => equipBorder(b.id)}
                      className="mt-2 w-full bg-emerald-500 text-white border-2 border-slate-950 rounded-lg py-1.5 font-display font-bold text-xs uppercase"
                    >
                      Équiper
                    </button>
                  )
                ) : b.price === 0 ? (
                  <button
                    data-testid={`equip-border-${b.id}`}
                    onClick={() => equipBorder(b.id)}
                    className="mt-2 w-full bg-emerald-500 text-white border-2 border-slate-950 rounded-lg py-1.5 font-display font-bold text-xs uppercase"
                  >
                    Équiper
                  </button>
                ) : (
                  <button
                    data-testid={`buy-border-${b.id}`}
                    onClick={() => purchaseBorder(b.id, b.price)}
                    disabled={!canAfford}
                    className={`mt-2 w-full ${canAfford ? "bg-yellow-400 text-slate-950" : "bg-slate-700 text-slate-400 cursor-not-allowed"} border-2 border-slate-950 rounded-lg py-1.5 font-display font-bold text-xs uppercase flex items-center justify-center gap-1`}
                  >
                    {canAfford ? <><Coins className="w-3 h-3" /> {b.price}</> : <><Lock className="w-3 h-3" /> {b.price}</>}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <h2 className="font-display font-bold text-lg mb-3 mt-6 text-white flex items-center gap-2">
          <ImageIcon className="w-5 h-5 text-sky-400" /> Bannières de profil
        </h2>
        <div data-testid="banners-grid" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 pb-6">
          {BANNERS.map((b) => {
            const isOwned = owned.has(b.id);
            const isEquipped = (profile?.equipped_banner || "default") === b.id;
            const canAfford = available >= b.price;
            const rarityClass = COSMETIC_RARITY_COLORS[b.rarity];
            return (
              <div
                key={b.id}
                data-testid={`banner-card-${b.id}`}
                className={`bg-slate-800 border-4 border-slate-950 rounded-2xl p-3 cartoon-shadow-lg-static flex flex-col ${isEquipped ? "ring-4 ring-yellow-400" : ""}`}
              >
                <div className={`w-full h-20 rounded-xl border-2 border-slate-950 ${b.css} mb-2`} />
                <p className="font-display font-bold text-sm text-center leading-tight">{b.name}</p>
                <p className={`text-[10px] font-bold uppercase tracking-wider text-center ${rarityClass}`}>{b.rarity}</p>
                {isOwned ? (
                  isEquipped ? (
                    <div className="mt-2 w-full bg-yellow-400 text-slate-950 border-2 border-slate-950 rounded-lg py-1.5 font-display font-bold text-xs uppercase flex items-center justify-center gap-1">
                      <Check className="w-3 h-3" /> Équipée
                    </div>
                  ) : (
                    <button
                      data-testid={`equip-banner-${b.id}`}
                      onClick={() => equipBanner(b.id)}
                      className="mt-2 w-full bg-emerald-500 text-white border-2 border-slate-950 rounded-lg py-1.5 font-display font-bold text-xs uppercase"
                    >
                      Équiper
                    </button>
                  )
                ) : b.price === 0 ? (
                  <button data-testid={`equip-banner-${b.id}`} onClick={() => equipBanner(b.id)} className="mt-2 w-full bg-emerald-500 text-white border-2 border-slate-950 rounded-lg py-1.5 font-display font-bold text-xs uppercase">Équiper</button>
                ) : (
                  <button
                    data-testid={`buy-banner-${b.id}`}
                    onClick={() => purchaseBorder(b.id, b.price)}
                    disabled={!canAfford}
                    className={`mt-2 w-full ${canAfford ? "bg-yellow-400 text-slate-950" : "bg-slate-700 text-slate-400 cursor-not-allowed"} border-2 border-slate-950 rounded-lg py-1.5 font-display font-bold text-xs uppercase flex items-center justify-center gap-1`}
                  >
                    {canAfford ? <><Coins className="w-3 h-3" /> {b.price}</> : <><Lock className="w-3 h-3" /> {b.price}</>}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MenuShop;
